<template>
  <app-wrapper>
    <app-header :loan="loan" medium-max-width="max-w-1000">
      <template #navigation>
        <div class="max-w-200">
          <h5 class="font-zen-dots text-white">
            <AppLogo/>
          </h5>
        </div>
      </template>
      <template #top>
        <div class="w-full flex items-center">
          <div class="flex-auto">
            Your desired loan amount
            <h2 class="mb-0 mt-2 md:mt-4 md:text-6xl">{{ loan.amount | money(0) }}</h2>
          </div>
          <div class="flex-initial">
            <i class="fa fa-check-circle text-success text-7xl"/>
          </div>
        </div>
      </template>
    </app-header>
    <app-body medium-max-width="max-w-500" footer>

      <template #title>
        Verify Identity
      </template>

      <template #text>
        <div>
          Confirm identity information against regulated data sources in seconds.
        </div>
      </template>

      <template #main>
        <div v-if="submitError" class="w-full max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
          <p class="text-red-700 text-sm font-medium">{{ submitError }}</p>
        </div>

        <!-- C3: a stale-but-clean prior success — the borrower's identity is already
             verified, but the state machine's own advancement never landed (or gave
             up long enough ago that the freshness grace window has passed). Never a
             fresh Plaid session (the vendor would only refuse the existing terminal
             attempt) and never an indefinite spinner — a minimal card that re-drives
             the existing bodyless completion endpoint. -->
        <div
            v-if="staleSuccessRecovery"
            data-testid="identity-stale-success-recovery"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="text-center">
            <div class="flex justify-center mb-4">
              <i class="fa fa-check-circle text-green-500 text-5xl"/>
            </div>
            <h3 class="text-xl font-semibold text-gray-800">Your identity is already verified</h3>
            <p class="text-gray-600 mt-2">
              We already confirmed your identity — continue your application to pick up where you left off.
            </p>
          </div>

          <div class="flex justify-center mt-6">
            <button
                @click="continueApplication"
                :disabled="loading"
                class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
            >
              <i v-if="loading" class="fa fa-circle-notch fa-spin mr-2"/>
              Continue application
            </button>
          </div>

          <p class="text-center text-gray-600 mt-6">
            Still stuck?
            <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
              Contact Support
            </a>
          </p>
        </div>
        <div
            v-else-if="! showBanking && ! showManual && !hasVerificationResults && hasUsableLinkToken"
            class="mt-7"
        >

          <div v-if="dataUpdated" class="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-center">
            <div class="flex justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-800">Your information has been updated successfully</h3>
            <p class="text-gray-600 mt-2">Please verify your identity to continue.</p>
          </div>

          <div @click.prevent="showBanking = true"
               class="bg-white border-gray-300 cursor-pointer shadow-lg border p-3 rounded-lg flex space-x-3">
            <div class="bg-terciary rounded-md px-3 flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                   class="text-action w-8 h-8">
                <path fill-rule="evenodd"
                      d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z"
                      clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-auto space-y-1 md:py-2">
              <p class="text-lg font-bold">Instant Connection</p>
              <p>Click here to securely verify your identity</p>
            </div>
            <div class="flex items-center justify-center">
              <span class="bg-green-100 text-green-500 rounded-lg p-2 mr-4 font-bold">FAST</span>
            </div>
          </div>
        </div>
        <!-- C3: no usable Link token and no actionable verificationResults either — a
             genuine render/session-opening error. Safe retry/support guidance, never
             an inert Link control with a null token underneath. -->
        <div
            v-else-if="! showBanking && ! showManual && !hasVerificationResults && !hasUsableLinkToken"
            data-testid="identity-unavailable-recovery"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10 text-center"
        >
          <h3 class="text-xl font-semibold text-gray-800">We couldn't load your verification</h3>
          <p class="text-gray-600 mt-2">Please refresh the page and try again.</p>
          <p class="text-gray-600 mt-4">
            Still stuck?
            <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
              Contact Support
            </a>
          </p>
        </div>
        <div v-else-if="showBanking && loan.banking_vendor_id === 2 && !hasVerificationResults" class="mt-7 space-y-7">

          <div v-if="plaidLoading" class="flex justify-center">
            <i class="fa fa-circle-notch fa-spin text-3xl"/>
          </div>

          <div @click.prevent="plaid.open()"
               class="flex cursor-pointer items-center bg-gray-50 shadow rounded-lg md:h-full">

            <div class="flex-auto py-6 px-6">
              <svg class="h-8" viewBox="0 0 126 48" xmlns="http://www.w3.org/2000/svg">
                <g fill="#111" fill-rule="evenodd">
                  <path
                      d="M66.248 16.268c-1.057-.889-2.861-1.333-5.413-1.333h-5.756v17.788h4.304v-5.575h1.928c2.34 0 4.056-.515 5.148-1.546 1.23-1.155 1.849-2.693 1.849-4.613 0-1.991-.687-3.565-2.06-4.721m-5.044 6.855h-1.821V18.96h1.636c1.99 0 2.985.698 2.985 2.094 0 1.378-.934 2.068-2.8 2.068M75.673 14.934h-4.488v17.788h9.69v-4.026h-5.202zM89.668 14.934l-7.05 17.788h4.832l.924-2.586H94.5l.845 2.586h4.886l-7-17.788zm-.053 11.601l1.849-6.08 1.82 6.08z"></path>
                  <path
                      d="M102.473 32.722h4.489V14.934h-4.489zM124.39 18.268a7.376 7.376 0 00-2.14-2.053c-1.355-.854-3.204-1.28-5.545-1.28h-5.914v17.787h6.918c2.5 0 4.506-.817 6.02-2.453 1.514-1.635 2.27-3.805 2.27-6.508 0-2.15-.537-3.981-1.61-5.493m-7.182 10.427h-1.927v-9.734h1.954c1.373 0 2.428.43 3.168 1.287.74.857 1.11 2.073 1.11 3.647 0 3.2-1.435 4.8-4.305 4.8M18.637 0L4.09 3.81.081 18.439l5.014 5.148L0 28.65l3.773 14.693 14.484 4.047 5.096-5.064 5.014 5.147 14.547-3.81 4.008-14.63-5.013-5.146 5.095-5.063L43.231 4.13 28.745.083l-5.094 5.063zM9.71 6.624l7.663-2.008 3.351 3.44-4.887 4.856zm16.822 1.478l3.405-3.383 7.63 2.132-6.227 6.187zM4.672 17.238l2.111-7.705 6.125 6.288-4.886 4.856zm29.547-1.243l6.227-6.189 1.986 7.74-3.404 3.384zm-15.502-.127l4.887-4.856 4.807 4.936-4.886 4.856zm-7.814 7.765l4.886-4.856 4.81 4.936-4.888 4.856zm15.503.127l4.886-4.856L36.1 23.84l-4.887 4.856zM4.57 29.927l3.406-3.385 4.807 4.937-6.225 6.186zm14.021 1.598l4.887-4.856 4.808 4.936-4.886 4.856zm15.502.128l4.887-4.856 3.351 3.439-2.11 7.705zm-24.656 8.97l6.226-6.189 4.81 4.936-3.406 3.385zm16.843-1.206l4.886-4.856 6.126 6.289-7.662 2.007z"></path>
                </g>
              </svg>
            </div>
            <div class="flex-auto">
              <p class="pr-4 md:pr-6 leading-5">Your information is secured by Plaid's bank level security
                standards.</p>
            </div>
          </div>
        </div>
        <!-- Scenario E: Document capture never completed — a session exists but Plaid
             did not receive a complete document set. Without this the borrower
             is parked on the processing spinner with nothing to do but resubmit. -->
        <div
            v-if="captureDidNotComplete"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="text-center">
            <div class="flex justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4l16 16" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-800 leading-relaxed">
              We couldn't complete your ID photo capture
            </h3>
            <p class="font-semibold text-gray-600 mt-2 leading-relaxed">
              Your verification started, but we didn't receive a complete set of ID photos, so
              there is nothing for us to check yet. This usually means the camera didn't open or
              the capture was interrupted.
            </p>
            <ul class="mt-4 text-gray-700 inline-block text-left">
              <li class="py-1">
                <i class="fa fa-check-circle text-green-500 mr-2"></i> Allow camera access when your browser asks
              </li>
              <li class="py-1">
                <i class="fa fa-check-circle text-green-500 mr-2"></i> Use your phone if this device has no camera
              </li>
              <li class="py-1">
                <i class="fa fa-check-circle text-green-500 mr-2"></i> Keep the whole document inside the frame
              </li>
            </ul>
          </div>

          <div class="flex justify-center mt-6">
            <button
                v-if="!hasReachedRetryLimit"
                @click="retryVerification"
                :disabled="retryLoading"
                class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
            >
              <i v-if="retryLoading" class="fa fa-circle-notch fa-spin mr-2"></i>
              Try Again
            </button>
            <p v-else class="text-gray-700 text-center">
              You've reached the maximum number of verification attempts.<br>
              Support can open one more for you.
            </p>
          </div>

          <p class="text-center text-gray-600 mt-6">
            Still can't get past this step?
            <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
              Contact Support
            </a>
          </p>
        </div>

        <div
            v-else-if="hasCorrectionPolicy || isCappedSupportRequired"
            data-testid="identity-profile-correction"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="text-center">
            <h3 class="text-xl font-semibold text-gray-800">Please review your information</h3>
            <p class="text-gray-600 mt-2">We need to confirm the highlighted application information before another verification attempt.</p>
          </div>

          <div v-if="correctionNeedsSupport || isCappedSupportRequired" class="text-center mt-6">
            <p class="text-gray-700">This verification needs support assistance before another attempt.</p>
            <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">Contact Support</a>
          </div>

          <div v-else class="space-y-4 mt-6">
            <div v-if="profileIssueFields.includes('ssn')">
              <label class="block text-sm font-medium text-zinc-700 mb-1">Social Security Number (SSN)</label>
              <input
                  type="password"
                  data-private="true"
                  v-model="formData.ssn"
                  autocomplete="off"
                  placeholder="XXX-XX-XXXX"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-action"
              />
              <p v-if="verificationResults.identity_profile_ssn_suffix" class="text-sm text-gray-500 mt-1">Current SSN ends in: {{ verificationResults.identity_profile_ssn_suffix }}</p>
            </div>
            <template v-if="profileIssueFields.includes('name') || profileIssueFields.includes('date_of_birth')">
              <label v-if="profileIssueFields.includes('name')" class="block text-sm font-medium text-zinc-700" for="profile-first-name">First name</label>
              <input v-if="profileIssueFields.includes('name')" id="profile-first-name" v-model="formData.first_name" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('name')" class="block text-sm font-medium text-zinc-700" for="profile-last-name">Last name</label>
              <input v-if="profileIssueFields.includes('name')" id="profile-last-name" v-model="formData.last_name" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('date_of_birth')" class="block text-sm font-medium text-zinc-700" for="profile-date-of-birth">Date of birth</label>
              <input v-if="profileIssueFields.includes('date_of_birth')" id="profile-date-of-birth" v-model="formData.date_of_birth" type="date" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
            </template>
            <template v-if="profileIssueFields.includes('phone') || profileIssueFields.includes('address')">
              <label v-if="profileIssueFields.includes('phone')" class="block text-sm font-medium text-zinc-700" for="profile-phone">Phone number</label>
              <input v-if="profileIssueFields.includes('phone')" id="profile-phone" v-model="contactForm.phone_number" type="tel" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('address')" class="block text-sm font-medium text-zinc-700" for="profile-address">Street address</label>
              <input v-if="profileIssueFields.includes('address')" id="profile-address" v-model="contactForm.address" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('address')" class="block text-sm font-medium text-zinc-700" for="profile-address2">Address line 2</label>
              <input v-if="profileIssueFields.includes('address')" id="profile-address2" v-model="contactForm.address2" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('address')" class="block text-sm font-medium text-zinc-700" for="profile-city">City</label>
              <input v-if="profileIssueFields.includes('address')" id="profile-city" v-model="contactForm.city" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              <label v-if="profileIssueFields.includes('address')" class="block text-sm font-medium text-zinc-700" for="profile-state">State</label>
              <select v-if="profileIssueFields.includes('address')" id="profile-state" v-model="contactForm.state" class="w-full border border-zinc-300 rounded-lg px-3 py-2">
                <option
                    v-if="contactForm.state && !(verificationResults.valid_states || []).includes(contactForm.state)"
                    :value="contactForm.state"
                >{{ contactForm.state }}</option>
                <option
                    v-for="st in (verificationResults.valid_states || [])"
                    :key="st"
                    :value="st"
                >{{ st }}</option>
              </select>
              <label v-if="profileIssueFields.includes('address')" class="block text-sm font-medium text-zinc-700" for="profile-zip">ZIP</label>
              <input v-if="profileIssueFields.includes('address')" id="profile-zip" v-model="contactForm.zip" type="text" class="w-full border border-zinc-300 rounded-lg px-3 py-2" />
            </template>
            <div class="flex justify-center gap-4">
              <button
                  @click="submitProfileCorrection"
                  :disabled="loading || !canSubmitProfileCorrection"
                  class="button w-full shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6 disabled:opacity-50"
              >
                <i v-if="loading" class="fa fa-circle-notch fa-spin mr-2"></i>
                Update & Verify Again
              </button>
              <!-- Legacy KYC SSN/name/DOB mismatches kept a decline option before the
                   composed card unified them; a captured-profile divergence never had one. -->
              <button
                  v-if="verificationResults?.identity_profile_issue == null"
                  @click="confirmUpdate(false)"
                  class="button
                     shadow-[0px_2px_4px_rgba(107,142,141,0.5)]
                     font-semibold text-lg
                     bg-red-400
                     focus:outline-none focus:ring-2 focus:ring-red-300
                     rounded text-white py-3 px-6
                     transition-colors duration-200"
              >
                Decline
              </button>
            </div>
          </div>
        </div>

        <!-- Scenario A: Extraction Failed (any field has no_data) - but NOT when SSN is also mismatched -->
        <div
            v-else-if="verificationResults?.status === 'failed' && hasExtractionFailure && !isSsnMismatched"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="flex items-center justify-center mb-6 space-x-3">
            <div class="text-center">
              <div class="flex justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 leading-relaxed">
                We couldn't read your document
              </h3>
              <p class="font-semibold text-gray-600 mt-2 leading-relaxed">
                We couldn't extract the following from your document:
              </p>
              <ul class="mt-3 text-gray-700 inline-block text-left">
                <li v-if="verificationResults.extraction_status?.name === 'no_data'" class="py-1 font-semibold">
                  <i class="fa fa-times-circle text-red-500 mr-2"></i> Name
                </li>
                <li v-if="verificationResults.extraction_status?.date_of_birth === 'no_data'" class="py-1 font-semibold">
                  <i class="fa fa-times-circle text-red-500 mr-2"></i> Date of Birth
                </li>
              </ul>
              <p class="text-gray-600 mt-4 leading-relaxed">
                Please retry verification with a clearer, high-quality photo of your document.
              </p>
            </div>
          </div>

          <!-- Actions based on session count -->
          <div class="flex justify-center gap-4 mt-6">
            <template v-if="verificationResults.session_count <= 1">
              <button
                  @click="retryVerification"
                  :disabled="retryLoading"
                  class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
              >
                <i v-if="retryLoading" class="fa fa-circle-notch fa-spin mr-2"></i>
                Try Again
              </button>
            </template>
            <template v-else>
              <div class="text-center">
                <p class="text-gray-700 mb-4">
                  You've reached the maximum number of retry attempts.<br>
                  Please contact support for assistance.
                </p>
                <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
                  Contact Support
                </a>
              </div>
            </template>
          </div>
        </div>

        <!-- Scenario B: SSN Mismatch (with optional Name/DOB mismatch or extraction failure) -->
        <div
            v-else-if="verificationResults?.status === 'failed' && isSsnMismatched"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="flex items-center justify-center mb-6 space-x-3">
            <div class="text-center">
              <h3 class="text-xl font-semibold text-gray-800">
                {{ ssnWithOtherIssues ? 'Verification issues found' : 'SSN verification failed' }}
              </h3>
              <p v-if="isWrongSsn" class="font-semibold text-gray-600 mt-2 leading-relaxed">
                We couldn't confirm the SSN you provided belongs to you. Please re-enter your Social Security Number.
              </p>
              <p v-else-if="verificationResults.session_count <= 1" class="font-semibold text-gray-600 mt-2 leading-relaxed">
                <template v-if="hasExtractionFailure">
                  We couldn't read some information from your document. Please enter your correct SSN:
                </template>
                <template v-else-if="isNameOrDobMismatched">
                  Please enter your correct SSN and confirm the extracted information:
                </template>
                <template v-else>
                  Please enter your correct Social Security Number:
                </template>
              </p>
            </div>
          </div>

          <!-- SSN Input - only show if retry is allowed -->
          <template v-if="verificationResults.session_count <= 1">
            <div class="mb-6">
              <label class="block text-sm font-medium text-zinc-700 mb-1">Social Security Number (SSN)</label>
              <input
                  type="password"
                  data-private="true"
                  v-model="formData.ssn"
                  autocomplete="off"
                  placeholder="XXX-XX-XXXX"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-action"
              />
              <p v-if="ssnValidationError" class="text-sm text-red-500 mt-1">
                {{ ssnValidationError }}
              </p>
              <p v-else-if="verificationResults.user_current?.ssn_last4" class="text-sm text-gray-500 mt-1">
                Current SSN ends in: {{ verificationResults.user_current.ssn_last4 }}
              </p>
            </div>

            <!-- Show extraction failure info if name/DOB couldn't be read -->
            <div v-if="hasExtractionFailure" class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p class="text-sm text-yellow-800 font-medium mb-2">
                The following couldn't be extracted from your document:
              </p>
              <ul class="text-sm text-yellow-700 mb-3">
                <li v-if="verificationResults.extraction_status?.name === 'no_data'" class="py-1">
                  <i class="fa fa-times-circle text-yellow-600 mr-2"></i> Name
                </li>
                <li v-if="verificationResults.extraction_status?.date_of_birth === 'no_data'" class="py-1">
                  <i class="fa fa-times-circle text-yellow-600 mr-2"></i> Date of Birth
                </li>
              </ul>
              <p class="text-sm text-yellow-800">
                Please ensure your next document photo is clear, well-lit, and all text is readable.
              </p>
            </div>

            <!-- Show extracted Name/DOB if available and mismatched (suppressed for wrong-SSN anomaly) -->
            <div v-else-if="isNameOrDobMismatched && hasExtractedData && !isWrongSsn" class="mb-6">
              <p class="text-sm font-medium text-zinc-700 mb-3">
                Please confirm you agree to update the following:
              </p>
              <dl class="grid grid-cols-1 gap-y-2 bg-gray-50 p-4 rounded-lg">
                <div v-if="isNameMismatched" class="flex justify-between items-center py-2 border-b border-zinc-200">
                  <dt class="text-sm font-medium text-zinc-600">Name</dt>
                  <dd class="text-sm text-zinc-900">
                    {{ verificationResults.extracted_data.given_name }}
                    {{ verificationResults.extracted_data.family_name }}
                  </dd>
                </div>
                <div v-if="isDobMismatched" class="flex justify-between items-center py-2">
                  <dt class="text-sm font-medium text-zinc-600">Date of Birth</dt>
                  <dd class="text-sm text-zinc-900">{{ verificationResults.extracted_data.date_of_birth }}</dd>
                </div>
              </dl>
            </div>

            <!-- Actions -->
            <div class="flex justify-center gap-4">
              <button
                  @click="submitSsnUpdateWithNameDob"
                  :disabled="!isValidSsnInput"
                  title="Enter a valid 9-digit SSN"
                  class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6 disabled:opacity-50"
              >
                <i v-if="loading" class="fa fa-circle-notch fa-spin mr-2"></i>
                {{ isNameOrDobMismatched && hasExtractedData ? 'Update All & Verify Again' : 'Update & Verify Again' }}
              </button>
              <button
                  @click="confirmUpdate(false)"
                  class="button
                     shadow-[0px_2px_4px_rgba(107,142,141,0.5)]
                     font-semibold text-lg
                     bg-red-400
                     focus:outline-none focus:ring-2 focus:ring-red-300
                     rounded text-white py-3 px-6
                     transition-colors duration-200"
                  >
                Decline
              </button>
            </div>
          </template>

          <!-- Max retry reached -->
          <div v-else class="text-center mt-6">
            <p class="text-gray-700 mb-4">
              You've reached the maximum number of retry attempts.<br>
              Please contact support for assistance.
            </p>
            <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
              Contact Support
            </a>
          </div>
        </div>

        <!-- Scenario C: Name/DOB Mismatch only (SSN matches - original behavior) -->
        <div
            v-else-if="verificationResults?.status === 'failed' && hasMismatch"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="flex items-center justify-center mb-6 space-x-3">
            <div class="text-center">
              <h3 class="text-xl font-semibold text-gray-800">
                Some information didn't match
              </h3>
              <p class="font-semibold text-gray-600 mt-2 leading-relaxed">
                Please confirm you agree to update the following:
              </p>
            </div>
          </div>

          <!-- Display extracted data (read-only) - only show mismatched fields -->
          <dl class="grid grid-cols-1 gap-y-2 mb-8">
            <div v-if="isNameMismatched" class="flex justify-between items-center py-4 border-b border-zinc-200">
              <dt class="text-lg font-medium text-zinc-700">Name</dt>
              <dd class="text-lg text-zinc-900">
                {{ verificationResults.extracted_data.given_name }}
                {{ verificationResults.extracted_data.family_name }}
              </dd>
            </div>
            <div v-if="isDobMismatched" class="flex justify-between items-center py-4 border-b border-zinc-200">
              <dt class="text-lg font-medium text-zinc-700">Date of Birth</dt>
              <dd class="text-lg text-zinc-900">{{ verificationResults.extracted_data.date_of_birth }}</dd>
            </div>
          </dl>

          <!-- Actions (original behavior) -->
          <div class="flex justify-center gap-4">
            <button
                @click="confirmUpdate(true)"
                :disabled="!hasValidExtractedDataForUpdate"
                class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update
            </button>
            <button
                @click="confirmUpdate(false)"
                class="button
                   shadow-[0px_2px_4px_rgba(107,142,141,0.5)]
                   font-semibold text-lg
                   bg-red-400
                   focus:outline-none focus:ring-2 focus:ring-red-300
                   rounded text-white py-3 px-6
                   transition-colors duration-200"
                >
              Decline
            </button>
          </div>
        </div>

        <!-- Scenario F: Address/Phone Mismatch (SSN + name + DOB all matched) -->
        <div
            v-else-if="isAddressOrPhoneMismatched"
            data-testid="identity-contact-correction"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="text-center mb-6">
            <h3 class="text-xl font-semibold text-gray-800">
              Some contact information didn't match
            </h3>
            <p v-if="hasReachedRetryLimit" class="font-semibold text-gray-600 mt-2 leading-relaxed">
              We couldn't verify your phone number and/or address. Please confirm or correct
              the details below. Since you've reached the maximum number of verification attempts,
              support will need to allow one more try after your update.
            </p>
            <p v-else class="font-semibold text-gray-600 mt-2 leading-relaxed">
              We couldn't verify your phone number and/or address. Please confirm or correct
              the details below and we'll try again.
            </p>
          </div>

          <template v-if="shouldShowContactUpdateForm">
            <div class="space-y-4 mb-8">
              <!-- Phone (only when phone mismatched) -->
              <div v-if="isPhoneMismatched">
                <label class="block text-sm font-medium text-zinc-700 mb-1">Phone number</label>
                <input
                    v-model="contactForm.phone_number"
                    type="tel"
                    class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder="Phone number"
                />
              </div>

              <!-- Address (only when address mismatched) -->
              <template v-if="isAddressMismatched">
                <div>
                  <label class="block text-sm font-medium text-zinc-700 mb-1">Street address</label>
                  <input
                      v-model="contactForm.address"
                      type="text"
                      class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                      placeholder="Street address"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-zinc-700 mb-1">Apt / Unit (optional)</label>
                  <input
                      v-model="contactForm.address2"
                      type="text"
                      class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                      placeholder="Apt, suite, unit"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-zinc-700 mb-1">City</label>
                  <input
                      v-model="contactForm.city"
                      type="text"
                      class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                      placeholder="City"
                  />
                </div>
                <div class="flex gap-3">
                  <div class="flex-1">
                    <label class="block text-sm font-medium text-zinc-700 mb-1">State</label>
                    <select
                        v-model="contactForm.state"
                        class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                    >
                      <option
                          v-if="contactForm.state && !(verificationResults.valid_states || []).includes(contactForm.state)"
                          :value="contactForm.state"
                      >{{ contactForm.state }}</option>
                      <option
                          v-for="st in (verificationResults.valid_states || [])"
                          :key="st"
                          :value="st"
                      >{{ st }}</option>
                    </select>
                  </div>
                  <div class="flex-1">
                    <label class="block text-sm font-medium text-zinc-700 mb-1">ZIP</label>
                    <input
                        v-model="contactForm.zip"
                        type="text"
                        class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action"
                        placeholder="ZIP"
                    />
                  </div>
                </div>
                <p class="text-xs text-gray-500 mt-1">
                  If you've moved out of our service area we'll connect you with another lender.
                </p>
              </template>
            </div>

            <div class="flex flex-col sm:flex-row justify-center gap-4">
              <button
                  @click="submitContactUpdate"
                  :disabled="loading || !canSubmitContactUpdate"
                  class="button w-full sm:w-auto shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i v-if="loading" class="fa fa-circle-notch fa-spin mr-2"></i>
                {{ hasReachedRetryLimit ? 'Update' : 'Update & Verify Again' }}
              </button>
              <button
                  @click="confirmUpdate(false)"
                  class="button
                     w-full sm:w-auto
                     shadow-[0px_2px_4px_rgba(107,142,141,0.5)]
                     font-semibold text-lg
                     bg-red-400
                     focus:outline-none focus:ring-2 focus:ring-red-300
                     rounded text-white py-3 px-6
                     transition-colors duration-200"
              >
                Decline
              </button>
            </div>
          </template>
          <template v-else>
            <div class="text-center">
              <div v-if="dataUpdated" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 class="text-lg font-semibold text-gray-800">Your information has been updated successfully</h3>
                <p class="text-gray-600 mt-2">
                  Please contact support so we can allow one more verification attempt.
                </p>
              </div>
              <p class="text-gray-700 mb-4">
                You've reached the maximum number of retry attempts.<br>
                Please contact support for assistance.
              </p>
              <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
                Contact Support
              </a>
            </div>
          </template>
        </div>

        <!-- Scenario D: Fallback — status is 'failed' but no specific error condition matched -->
        <div
            v-else-if="verificationResults?.status === 'failed'"
            class="w-full max-w-md mx-auto bg-white rounded-[16px] shadow-lg p-8 mt-10"
        >
          <div class="text-center">
            <h3 class="text-xl font-semibold text-gray-800 leading-relaxed">
              Your identity verification failed.
            </h3>
            <p class="font-semibold text-gray-600 mt-2 leading-relaxed">
              Please try again.
            </p>
          </div>

          <div class="flex justify-center gap-4 mt-6">
            <template v-if="verificationResults.session_count <= 1">
              <button
                  @click="retryVerification"
                  :disabled="retryLoading"
                  class="button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
              >
                <i v-if="retryLoading" class="fa fa-circle-notch fa-spin mr-2"></i>
                Try Again
              </button>
            </template>
            <template v-else>
              <div class="text-center">
                <p class="text-gray-700 mb-4">
                  You've reached the maximum number of retry attempts.<br>
                  Please contact support for assistance.
                </p>
                <a :href="'mailto:' + supportEmail" class="text-action underline font-semibold">
                  Contact Support
                </a>
              </div>
            </template>
          </div>
        </div>

        <confirm
            :open="openConfirm"
            :confirm-message="getConfirmMessage"
            @confirmed="submit"
            @close="openConfirm = false"
        />
      </template>

    </app-body>
  </app-wrapper>
</template>

<script>

import AppLogo from './../../../components/AppLogo';
import { analytics } from '../../../analytics';
import Confirm from "../agreed/Confirm.vue";

export default {
  name: 'IdentityVerification',
  mixins: [
    require('../../../mixins/InteractsWithGtag').default,
    require('../../../mixins/PageViewAnalytics').default
  ],
  components: {
    Confirm,
    AppLogo
  },
  props: {
    user: {},
    loan: {},
    manual: {
      type: Boolean,
      default: false
    },
    mxWidgetUrl: {},
    plaidLinkToken: {
      type: String,
      default: null
    },
    verificationResults: {
      type: [Object, Array],
      default: () => ({})
    },
    supportEmail: {
      type: String,
      default: ''
    },
    dataUpdated: {
      type: Boolean,
      default: false
    },
    staleSuccessRecovery: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      openConfirm: false,
      gTagPageName: 'VERIFY_IDENTITY',
      gEventType: 'MarketPlaceSignUp',
      showBanking: false,
      showManual: false,
      plaid: null,
      institution_id: null,
      institution_code: null,
      loading: false,
      fileName: null,
      plaidLoading: false,
      isPaystubOutdated: false,
      paystubDate: '',
      pendingAccept: false,
      retryLoading: false,
      submitError: '',
      ssnValidationError: '',
      formData: {
        first_name: '',
        last_name: '',
        date_of_birth: '',
        ssn: ''
      },
      contactForm: {
        phone_number: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: ''
      },
      manualForm: {
        errors: null,
        account_number: '',
        account_number_confirmation: '',
        routing_number: '',
        routing_number_confirmation: '',
        official_name: ''
      }
    }
  },
  
  watch: {
    showBanking() {
      setTimeout(() => {
        // Followup: state can change between scheduling this timeout and it firing
        // (the recovery card taking over, the token being cleared, or the borrower
        // navigating away from the Instant Connection card entirely) — re-check
        // immediately before construction so window.Plaid.create() is never launched
        // for a stale/no-longer-applicable render.
        if (!this.showBanking || this.staleSuccessRecovery || !this.hasUsableLinkToken) {
          return;
        }

        this.plaid = window.Plaid.create({
          token: this.plaidLinkToken,
          onSuccess: (metadata) => {
            this.plaidLoading = true;

            analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaidSuccess({
              user_id: this.user.id,
              loan_id: this.loan?.id ?? null,
              page_name: this.gTagPageName,
              loan_status: this.loan?.status ?? null,
            });
            
            this.$http.post(`/signup/welcome/${this.loan.id}`).then(() => {
              this.plaidLoading = false;
              this.continue();
            }).catch(error => {
              this.plaidLoading = false;
            })
          },
          onEvent: (event, metadata) => {
              if (event == 'ERROR') {
                  window.Bugsnag.notify('fail IdentityVerification  : ' + JSON.stringify({
                    error: metadata.error_code + ' (' + metadata.error_message + ')',
                    loan_id: this.loan?.id,
                    user_id: this.user?.id,
                  }));
              }

              this.$http?.post('/api/plaid/link-events', {
                  event_name: event,
                  loan_id: this.loan?.id ?? null,
                  institution_name: metadata.institution_name ?? metadata.institution?.name ?? null,
                  error_type: metadata.error_type ?? null,
                  error_code: metadata.error_code ?? null,
                  error_message: metadata.error_message ?? null,
                  link_session_id: metadata.link_session_id ?? null,
                  request_id: metadata.request_id ?? null,
                  exit_status: metadata.exit_status ?? null,
                  metadata: metadata,
                  event_time: new Date().toISOString(),
              }).catch(() => {});
          },
          onExit: (error, metadata) => {
            this.plaidLoading = false;
            
            window.Bugsnag.notify('fail IdentityVerification  : ' + JSON.stringify({
              error: error,
              status: metadata.status,
              link_session_id: metadata.link_session_id,
              plaid_request_id: metadata.request_id,
            }));

            analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaidExit({
              user_id: this.user?.id ?? null,
              loan_id: this.loan?.id ?? null,
              loan_status: this.loan?.status ?? null,
              page_name: this.gTagPageName,
              error: error,
              status: metadata.status,
              link_session_id: metadata.link_session_id,
              plaid_request_id: metadata.request_id,
            });
            
            window.location.reload();
          },
        });

        analytics.dinamicPageEvents(this.gEventType, this.gTagPageName).handlePlaid({
          user_id: this.user?.id ?? null,
          loan_status: this.loan?.status ?? null,
          loan_id: this.loan?.id ?? null,
          page_name: this.gTagPageName,
        });

        this.plaid.open();
      }, 150);
    }
  },

  methods: {
    confirmUpdate(accept) {
      this.pendingAccept = accept;
      this.openConfirm   = true;
    },

    async submit() {
      this.openConfirm = false;
      this.loading     = true;
      this.submitError = '';

      try {
        const payload = {
          update_identity: this.pendingAccept ? 'update' : 'decline',
          report_id: this.verificationResults?.report_id || null,
        };

        if (this.pendingAccept) {
          const extracted = this.verificationResults?.extracted_data || {};
          if (extracted.given_name) payload.first_name = extracted.given_name;
          if (extracted.family_name) payload.last_name = extracted.family_name;
          if (extracted.date_of_birth) payload.date_of_birth = extracted.date_of_birth;
        }

        await this.$http.post(`/signup/welcome/${this.loan.id}`, payload);
        window.location.reload();
      } catch (err) {
        this.loading = false;
        this.submitError = err.response?.data?.error || 'Something went wrong. Please try again.';
      }
    },

    async submitSsnUpdate() {
      this.loading = true;

      try {
        // Clear any previous validation error
        this.ssnValidationError = '';

        // SSN mismatch: update SSN and trigger re-verification
        await this.$http.post(`/signup/welcome/${this.loan.id}`, {
          update_identity: 'update_ssn',
          report_id: this.verificationResults?.report_id || null,
          ssn: this.formData.ssn,
        });
        window.location.reload();
      } catch (err) {
        this.loading = false;
        // Show validation error from server or generic message
        if (err.response?.data?.error) {
          this.ssnValidationError = err.response.data.error;
        } else {
          this.ssnValidationError = 'Something went wrong. Please try again.';
        }
      }
    },

    async submitSsnUpdateWithNameDob() {
      this.loading = true;

      try {
        // Clear any previous validation error
        this.ssnValidationError = '';

        // SSN mismatch with optional name/DOB update
        // If name/DOB is also mismatched and we have extracted data, include flag to update them
        const payload = {
          update_identity: 'update_ssn',
          report_id: this.verificationResults?.report_id || null,
          ssn: this.formData.ssn,
        };

        // If name/DOB is mismatched and we have extracted data, include the values
        if (this.isNameOrDobMismatched && this.hasExtractedData) {
          payload.update_name_dob = true;
          const extracted = this.verificationResults?.extracted_data || {};
          if (extracted.given_name) payload.first_name = extracted.given_name;
          if (extracted.family_name) payload.last_name = extracted.family_name;
          if (extracted.date_of_birth) payload.date_of_birth = extracted.date_of_birth;
        }

        await this.$http.post(`/signup/welcome/${this.loan.id}`, payload);
        window.location.reload();
      } catch (err) {
        this.loading = false;
        // Show validation error from server or generic message
        if (err.response?.data?.error) {
          this.ssnValidationError = err.response.data.error;
        } else {
          this.ssnValidationError = 'Something went wrong. Please try again.';
        }
      }
    },
    
    async submitContactUpdate() {
      this.loading = true;
      this.submitError = '';

      try {
        // Reuses update_identity:'update'. Because the address/phone gate matched before
        // name/dob on the backend, the handler routes this to applyContactDataFromRequest.
        const payload = {
          update_identity: 'update',
          report_id: this.verificationResults?.report_id || null,
        };

        if (this.isPhoneMismatched) {
          payload.phone_number = this.contactForm.phone_number;
        }
        if (this.isAddressMismatched) {
          payload.address = this.contactForm.address;
          payload.address2 = this.contactForm.address2;
          payload.city = this.contactForm.city;
          payload.state = this.contactForm.state;
          payload.zip = this.contactForm.zip;
        }

        await this.$http.post(`/signup/welcome/${this.loan.id}`, payload);
        window.location.reload();
      } catch (err) {
        this.loading = false;
        this.submitError = err.response?.data?.error || 'Something went wrong. Please try again.';
      }
    },

    async submitProfileCorrection() {
      this.loading = true;
      this.submitError = '';

      try {
        const fields = this.correctionPolicy.fields || [];
        const payload = {
          update_identity: this.correctionPolicy.action,
          report_id: this.verificationResults?.report_id || null,
        };

        if (fields.includes('ssn')) {
          payload.ssn = this.formData.ssn;
        }
        if (fields.includes('name')) {
          payload.first_name = this.formData.first_name;
          payload.last_name = this.formData.last_name;
        }
        if (fields.includes('date_of_birth')) {
          payload.date_of_birth = this.formData.date_of_birth;
        }
        if (fields.includes('phone')) {
          payload.phone_number = this.contactForm.phone_number;
        }
        if (fields.includes('address')) {
          payload.address = this.contactForm.address;
          payload.address2 = this.contactForm.address2;
          payload.city = this.contactForm.city;
          payload.state = this.contactForm.state;
          payload.zip = this.contactForm.zip;
        }

        await this.$http.post(`/signup/welcome/${this.loan.id}`, payload);
        window.location.reload();
      } catch (err) {
        this.loading = false;
        this.submitError = err.response?.data?.error || 'Something went wrong. Please try again.';
      }
    },
    refresh() {
      window.location.reload();
    },

    async continueApplication() {
      // C3: the EXISTING bodyless completion endpoint — it performs its own locked
      // fresh state/report checks and reloads on success, exactly like the Plaid
      // widget's own onSuccess handler above; no new session is opened here.
      this.loading = true;
      this.submitError = '';

      try {
        await this.$http.post(`/signup/welcome/${this.loan.id}`);
        window.location.reload();
      } catch (err) {
        this.loading = false;
        this.submitError = err.response?.data?.error || 'Something went wrong. Please try again.';
      }
    },

    continue() {
      this.loading = false;

      window.location.reload();
    },
    initFormData() {
      // Current application data is the only preselected correction source. Extracted
      // document values are not silently applied or submitted as borrower choices.
      const userCurrent = this.verificationResults?.user_current || {};

      this.formData.first_name = userCurrent.first_name || '';
      this.formData.last_name = userCurrent.last_name || '';
      this.formData.date_of_birth = userCurrent.date_of_birth || '';
      this.formData.ssn = '';

      // Pre-fill the editable contact form with the user's current values so they only
      // have to fix the typo (phone is never on the ID; the ID address is the wrong one).
      this.contactForm.phone_number = userCurrent.phone_number || '';
      this.contactForm.address = userCurrent.address || '';
      this.contactForm.address2 = userCurrent.address2 || '';
      this.contactForm.city = userCurrent.city || '';
      this.contactForm.state = userCurrent.state || '';
      this.contactForm.zip = userCurrent.zip || '';
    },
    async retryVerification() {
      this.retryLoading = true;

      try {
        await this.$http.post(`/signup/welcome/${this.loan.id}`, {
          retry_verification: true,
          report_id: this.verificationResults?.report_id || null,
        });
        window.location.reload();
      } catch (err) {
        this.retryLoading = false;
        alert('Failed to retry verification. Please try again.');
      }
    },
  },
  computed: {
    getConfirmMessage() {
      return this.pendingAccept
        ? 'Are you sure you want to update your information?'
        : 'Are you sure you want to decline the update? If we are unable to match your name (or whatever part is being updated) then your loan will be declined';
    },
    ssnTokens() {
      return {
        '#': { pattern: /\d/ },
        '*': { pattern: /[*\d]/ }
      };
    },
    isValidSsnInput() {
      return /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/.test(this.formData.ssn || '');
    },
    hasExtractionFailure() {
      const extractionStatus = this.verificationResults?.extraction_status;
      if (!extractionStatus) return false;
      return extractionStatus.name === 'no_data' || extractionStatus.date_of_birth === 'no_data';
    },
    hasMismatch() {
      const kycCheck = this.verificationResults?.kyc_check;
      if (!kycCheck) return false;
      const mismatchStatuses = ['partial_match', 'no_match'];
      return mismatchStatuses.includes(kycCheck.name_summary) ||
             mismatchStatuses.includes(kycCheck.date_of_birth_summary) ||
             mismatchStatuses.includes(kycCheck.id_number_summary);
    },
    isNameMismatched() {
      const summary = this.verificationResults?.kyc_check?.name_summary;
      return ['partial_match', 'no_match'].includes(summary);
    },
    isDobMismatched() {
      const summary = this.verificationResults?.kyc_check?.date_of_birth_summary;
      return ['partial_match', 'no_match'].includes(summary);
    },
    isSsnMismatched() {
      if (this.verificationResults?.is_wrong_ssn === true) return true;
      const summary = this.verificationResults?.kyc_check?.id_number_summary;
      return ['partial_match', 'no_match'].includes(summary);
    },
    isWrongSsn() {
      return this.verificationResults?.is_wrong_ssn === true;
    },
    isNameOrDobMismatched() {
      if (this.isWrongSsn) return false;
      return this.isNameMismatched || this.isDobMismatched;
    },
    isAddressMismatched() {
      const summary = this.verificationResults?.kyc_check?.address_summary;
      return ['partial_match', 'no_match'].includes(summary);
    },
    isPhoneMismatched() {
      const summary = this.verificationResults?.kyc_check?.phone_number_summary;
      return ['partial_match', 'no_match'].includes(summary);
    },
    isAddressOrPhoneMismatched() {
      // Followup: unlike Scenarios A-D (already gated behind status === 'failed' in
      // the template), this card has no such v-else-if guard of its own — a leftover
      // partial kyc_check node on an undecided (active/pending_review/canceled/
      // expired) observation must never surface it either. Only a decided outcome
      // carries correction authority for the raw per-field KYC summaries.
      if (!this.hasDecidedOutcome) return false;
      // Only when SSN / name / DOB are all clean — those scenarios take precedence.
      if (this.isSsnMismatched || this.isNameOrDobMismatched) return false;
      return this.isAddressMismatched || this.isPhoneMismatched;
    },
    hasDecidedOutcome() {
      return ['success', 'failed'].includes(this.verificationResults?.status);
    },
    isCappedSupportRequired() {
      // The session-limit branch never opened a session at all -- status is
      // deliberately null, not a decided outcome -- so this must stay separate from
      // hasCorrectionPolicy/hasDecidedOutcome rather than relaxing either of those
      // guards. Narrowly scoped to the exact shape that branch emits so no other
      // undecided report can ever reach this card through it.
      return this.verificationResults?.status === null
        && this.correctionPolicy.has_reached_session_limit === true
        && this.correctionPolicy.requires_support === true;
    },
    canSubmitContactUpdate() {
      // Require the mismatched field(s) to be non-empty before enabling submit.
      if (this.isPhoneMismatched && !this.contactForm.phone_number) return false;
      if (this.isAddressMismatched) {
        if (!this.contactForm.address || !this.contactForm.city
          || !this.contactForm.state || !this.contactForm.zip) {
          return false;
        }
      }
      return true;
    },
    hasReachedRetryLimit() {
      return Number(this.verificationResults?.session_count || 0) > 1;
    },
    shouldShowContactUpdateForm() {
      return !this.hasReachedRetryLimit || !this.dataUpdated;
    },
    ssnWithOtherIssues() {
      if (this.isWrongSsn) return false;
      return this.isSsnMismatched && (this.hasExtractionFailure || this.isNameOrDobMismatched);
    },
    hasExtractedData() {
      const extracted = this.verificationResults?.extracted_data;
      return extracted && (extracted.given_name || extracted.family_name || extracted.date_of_birth);
    },
    hasValidExtractedDataForUpdate() {
      // Check if we have the required extracted data for the fields that are mismatched
      // If name is mismatched, we need extracted name; if DOB is mismatched, we need extracted DOB
      const extracted = this.verificationResults?.extracted_data;
      if (!extracted) return false;

      // If name is mismatched, check we have at least given_name or family_name
      if (this.isNameMismatched && !extracted.given_name && !extracted.family_name) {
        return false;
      }

      // If DOB is mismatched, check we have extracted DOB
      if (this.isDobMismatched && !extracted.date_of_birth) {
        return false;
      }

      return true;
    },
    hasVerificationResults() {
      // Check if verificationResults has actual data (not null, not empty array, not empty object)
      if (!this.verificationResults) return false;
      if (Array.isArray(this.verificationResults) && this.verificationResults.length === 0) return false;
      if (typeof this.verificationResults === 'object' && Object.keys(this.verificationResults).length === 0) return false;
      return this.verificationResults.status === 'failed'
        || this.isAddressOrPhoneMismatched
        || this.captureDidNotComplete
        || this.hasCorrectionPolicy
        || this.isCappedSupportRequired;
    },
    correctionPolicy() {
      return this.verificationResults?.identity_correction_policy || {};
    },
    hasCorrectionPolicy() {
      // Followup: defense-in-depth alongside the server's own decided-outcome gate on
      // identity_correction_policy.fields (which is already empty for any undecided
      // report) -- this predicate must never authorize a correction card off an
      // undecided observation even if some other path ever populated fields early.
      if (!this.hasDecidedOutcome) return false;
      const fields = this.correctionPolicy.fields;
      if (!Array.isArray(fields) || fields.length === 0) return false;
      // A captured-profile divergence has always used the composed card.
      if (this.verificationResults?.identity_profile_issue != null) return true;
      // Legacy KYC-only issues touching SSN and/or name/DOB must also use the single
      // update_profile contract — the older per-scenario forms cannot submit a combined
      // SSN+name/DOB correction (or a name-only correction alongside extracted DOB data)
      // without the server rejecting the extra fields it no longer allow-lists for those
      // actions. Address/phone-only legacy mismatches keep the dedicated contact form so
      // the existing "save contact data, wait for admin" cap exception (which sends
      // update_identity: 'update') still applies.
      return fields.some((field) => ['ssn', 'name', 'date_of_birth'].includes(field));
    },
    profileIssueFields() {
      return this.correctionPolicy.fields || [];
    },
    correctionNeedsSupport() {
      return this.correctionPolicy.requires_support === true;
    },
    canSubmitProfileCorrection() {
      if (!this.correctionPolicy.action) return false;
      if (this.profileIssueFields.includes('ssn') && !this.isValidSsnInput) return false;
      if (this.profileIssueFields.includes('name') && (!this.formData.first_name || !this.formData.last_name)) return false;
      if (this.profileIssueFields.includes('date_of_birth') && !this.formData.date_of_birth) return false;
      if (this.profileIssueFields.includes('phone') && !this.contactForm.phone_number) return false;
      if (this.profileIssueFields.includes('address') && (!this.contactForm.address || !this.contactForm.city || !this.contactForm.state || !this.contactForm.zip)) return false;
      return true;
    },
    captureDidNotComplete() {
      // Plaid report classification belongs to the backend; the UI only renders the
      // resulting actionable state.
      return this.verificationResults?.capture_incomplete === true;
    },
    hasUsableLinkToken() {
      // C3: never offer a Link launch without a usable token — including a genuine
      // render/session-opening error, where the backend caught an exception and
      // returned neither a token nor actionable verificationResults.
      return !!this.plaidLinkToken;
    }
  },
  mounted() {
    this.initFormData();
  }
}
</script>
